import { useSyncExternalStore } from 'react';

const NAV_MINIMIZED_KEY = 'mastodon_nav_minimized';

/**
 * Whether the desktop navigation panel is collapsed to icons.
 *
 * This deliberately holds TWO values rather than one boolean:
 *
 * - `preference` is the user's own choice, made with the collapse handle and
 *   persisted per browser. It is the only one that survives a reload.
 * - `auto` is a temporary collapse asked for by a surface that wants the
 *   width — today only a live room (ticket 29), for as long as the viewer is
 *   in it.
 *
 * The panel is collapsed when EITHER is set, and a room may never write to
 * `preference`. Joining a room must not silently rewrite what the user chose
 * for the rest of the site.
 *
 * The one rule that makes it feel right rather than fighty: expanding always
 * wins. If someone opens the panel while they are inside a room, that clears
 * the room's collapse too, so the room cannot immediately shut it again.
 */
let preference = ((): boolean => {
  try {
    return localStorage.getItem(NAV_MINIMIZED_KEY) === '1';
  } catch {
    // Private mode, or storage disabled — the toggle still works in-session.
    return false;
  }
})();

let auto = false;

const listeners = new Set<() => void>();

const emit = (): void => {
  for (const listener of listeners) listener();
};

export const subscribeNavMinimized = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The panel is collapsed when either value is set. */
export const getNavMinimized = (): boolean => preference || auto;

const getSnapshot = getNavMinimized;

// Never collapsed on the server: the panel renders expanded and the client
// takes over on hydration.
const getServerSnapshot = (): boolean => false;

/** Flip the collapsed state from the handle. Persists the user's choice. */
export const toggleNavMinimized = (): void => {
  const next = !getSnapshot();

  preference = next;

  // Expanding beats a room's auto-collapse, so the room does not re-close it.
  if (!next) auto = false;

  try {
    localStorage.setItem(NAV_MINIMIZED_KEY, next ? '1' : '0');
  } catch {
    // Private mode — the toggle still works for this session.
  }

  emit();
};

/**
 * Ask for (or release) a temporary collapse. Call with `true` on entering a
 * surface that wants the width and `false` on leaving it — the caller owns
 * both halves, so an unmount always releases it.
 */
export const setNavAutoCollapsed = (value: boolean): void => {
  if (auto === value) return;
  auto = value;
  emit();
};

export const useNavMinimized = (): boolean =>
  useSyncExternalStore(subscribeNavMinimized, getSnapshot, getServerSnapshot);

/** Test-only: drop both values back to their defaults. */
export const resetNavMinimizedForTests = (): void => {
  preference = false;
  auto = false;
  emit();
};
